import { useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import { cloneDeep } from "lodash";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import Flex from "@/components/Flex";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import Insurance from "@/components/app/provider/patient/add-new-patient/Insurance";
import StyledModal from "@/components/globalModal/StyledModal";
import {
  INSURANCE_IDENTIFIER_SHAPE,
  SUBSCRIBER_SHAPE,
} from "@/components/app/provider/patient/add-new-patient/insurance/insurance-validation";
import { INITIAL_PATIENT_INSURANCE } from "@/constants/provider/patient/add-new-patient/add-new-patient-form";
import { DisclosureHost } from "@/hooks/use-disclosure";
import useCurrentUser from "@/hooks/local-storage/use-current-user";
import useSelectedPatient from "@/hooks/local-storage/use-selected-patient";
import { useSnackbar } from "@/hooks/useSnackbar";
import { useCreatePatientInsurance } from "@/queries/insurance/use-create-patient-insurance";
import { useGetInsuranceByPatientId } from "@/queries/insurance/use-get-patient-insurance-patient-uuid";
import { useUpdatePatientInsurance } from "@/queries/insurance/use-update-patient-insurance";
import { PatientInsurance } from "@/services";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getUnNecessaryTransformData,
  getXtenantId,
} from "@/utils/utils";
import { getViewAs } from "@/utils/auth-storage";

interface UpsertInsuranceFormDrawerProps {
  disClosureHost: DisclosureHost;
  title?: string;
}

type InsuranceForm = {
  patientInsurances: PatientInsurance &
    {
      planType?: any;
      planName?: any;
      planId?: any;
      insurancePayer: any;
      provider?: { [key: string]: string };
    }[];
};

const UpsertInsuranceFormModal = ({
  disClosureHost,
  title,
}: UpsertInsuranceFormDrawerProps) => {
  const { patientId } = useParams() as { patientId: any };
  const [me] = useCurrentUser();
  const [patientData] = useSelectedPatient();
  const isPatientPortalView = getViewAs() === "patient";
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const isEditInsurance = !!disClosureHost?.data;

  const { mutateAsync: updatePatientInsurance } = useUpdatePatientInsurance();
  const { mutateAsync: createPatientInsurance } = useCreatePatientInsurance();
  const { data: insuranceData } = useGetInsuranceByPatientId(
    isPatientPortalView ? me?.uuid : patientId
  );

  const formik = useFormik<InsuranceForm>({
    initialValues: disClosureHost?.data ?? {
      patientInsurances: isEditInsurance
        ? ((insuranceData as any)?.content ?? [])
        : [INITIAL_PATIENT_INSURANCE],
    },
    validationSchema: Yup.object({
      patientInsurances: Yup.array().of(
        Yup.object({
          ...INSURANCE_IDENTIFIER_SHAPE,
          ...SUBSCRIBER_SHAPE,
          // Effective Start Date is restricted at selection time via the date
          // picker's minDate (set to Patient DOB), so no Yup validation here.
        })
      ),
    }),
    onSubmit,
    enableReinitialize: true,
  });

  function onSubmit(values: InsuranceForm) {
    const copiedValues: any = cloneDeep(values);
    copiedValues.patientInsurances = values.patientInsurances?.map((it) => ({
      ...it,
      patient: {
        [isPatientPortalView ? me?.uuid : patientId]: "",
      },
      provider: it?.provider
        ? {
            [it?.provider.value]: `${it.provider.label}`,
          }
        : undefined,
      insurancePayer: {
        ...it.insurancePayer,
        payerId: it?.insurancePayer?.payerId?.value,
      },
      planId: it?.planId?.value,
      planName: it?.planId?.label,
      planType: it?.planType?.trim()?.length > 0 ? it?.planType : undefined,
    }));

    const transFormData: any = getUnNecessaryTransformData(
      ["languages"],
      copiedValues
    );

    const payload = {
      patientInsurance: isEditInsurance
        ? transFormData.patientInsurances[0]
        : (transFormData.patientInsurances as any),
      xTENANTID: getXtenantId() ?? undefined,
    };

    showToastAndReset(
      isEditInsurance
        ? updatePatientInsurance(payload as any)
        : createPatientInsurance(payload as any)
    );
  }

  const handleClose = () => {
    formik.resetForm();
    disClosureHost?.close();
  };

  const showToastAndReset = (apiCall: Promise<any>) => {
    apiCall
      ?.then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        queryClient.invalidateQueries({
          queryKey: ["get-insurance-by-patient-id"],
        });
        queryClient.invalidateQueries({
          queryKey: ["patient"],
        });
        handleClose();
      })
      .catch((err) => showSnackbar(getErrorSnackbar(err)));
  };

  return (
    <StyledModal
      open={disClosureHost.opened}
      onClose={handleClose}
      dialogTitle={title}
      dialogAction={
        <Flex gap={2}>
          <StyledButtonV2
            variant="secondary"
            label="Cancel"
            onClick={handleClose}
          />
          <StyledButtonV2
            variant="primary"
            label="Save"
            onClick={formik.submitForm}
          />
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "50vw",
          height: "70vh",
        },
      }}
    >
      <Insurance
        formik={formik as any}
        needSectionLabel={false}
        addOneInsurance={false}
        patientData={patientData}
      />
    </StyledModal>
  );
};

export default UpsertInsuranceFormModal;
